import { DataQueryProps, DriverScoreRangeLimits, PreDefinedRange } from '@ti/contracts'
import { DateRange } from '@ti/core/common/contracts'
import moment from '@ti/core/contracts/moment'

const LEGACY_DRIVER_SCORE_TIMEZONES: Readonly<Record<string, string>> = {
  EST: 'America/New_York',
}

/** Mirrors the Fleet write contract: known compatibility mappings are canonicalized while
 * every other valid Moment timezone retains its own semantics. */
export const resolveDriverScoreTimezone = (timezone?: string | null): string => {
  const candidate = timezone?.trim()
  const legacyTimezone = candidate
    ? LEGACY_DRIVER_SCORE_TIMEZONES[candidate.toUpperCase()]
    : undefined

  if (legacyTimezone) {
    return legacyTimezone
  }

  const zone = candidate ? moment.tz.zone(candidate) : null
  return zone?.name ?? 'UTC'
}

export interface DriverScoreRangeOptions {
  /**
   * The date picker converts custom ranges into the fleet timezone only while a timezone
   * override is shown. Otherwise its dates are browser wall-clock values; they are re-read in
   * the fleet timezone because equal offsets today do not mean equal offsets on the selected
   * dates.
   */
  customRangeInFleetTimezone?: boolean
}

/** Reads a browser wall-clock Date as the same wall-clock time in `timezone`. */
const readWallClockIn = (date: Date, timezone: string): Date =>
  moment
    .tz(
      [
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds(),
      ],
      timezone,
    )
    .toDate()

export const getDriverScoreDateRange = (
  dateRange: DateRange | PreDefinedRange | undefined,
  defaultDateRange: PreDefinedRange | undefined,
  defaultDateRangeFn: (() => DateRange | PreDefinedRange) | undefined,
  timezone: string,
  options: DriverScoreRangeOptions = {},
): DateRange | undefined => {
  const selectedRange = dateRange ?? defaultDateRangeFn?.() ?? defaultDateRange

  if (Array.isArray(selectedRange)) {
    return options.customRangeInFleetTimezone
      ? selectedRange
      : [readWallClockIn(selectedRange[0], timezone), readWallClockIn(selectedRange[1], timezone)]
  }

  if (selectedRange === PreDefinedRange.AllTime) {
    return undefined
  }

  const today = moment.tz(timezone).startOf('day')
  const endOfToday = today.clone().endOf('day')

  switch (selectedRange ?? PreDefinedRange.Today) {
    case PreDefinedRange.Today:
      return [today.toDate(), endOfToday.toDate()]
    case PreDefinedRange.Tomorrow:
      return [today.clone().add(1, 'day').toDate(), endOfToday.clone().add(1, 'day').toDate()]
    case PreDefinedRange.Yesterday:
      return [
        today.clone().subtract(1, 'day').toDate(),
        today.clone().subtract(1, 'millisecond').toDate(),
      ]
    case PreDefinedRange.Last7Days:
      return [today.clone().subtract(6, 'days').toDate(), endOfToday.toDate()]
    case PreDefinedRange.Last30Days:
      return [today.clone().subtract(29, 'days').toDate(), endOfToday.toDate()]
    case PreDefinedRange.Prev7Days:
      return [
        today.clone().subtract(7, 'days').toDate(),
        today.clone().subtract(1, 'millisecond').toDate(),
      ]
    case PreDefinedRange.Prev14Days:
      return [
        today.clone().subtract(14, 'days').toDate(),
        today.clone().subtract(1, 'millisecond').toDate(),
      ]
    case PreDefinedRange.Prev30Days:
      return [
        today.clone().subtract(30, 'days').toDate(),
        today.clone().subtract(1, 'millisecond').toDate(),
      ]
    case PreDefinedRange.PrevMonth: {
      const previousMonth = today.clone().subtract(1, 'month')
      return [
        previousMonth.clone().startOf('month').toDate(),
        previousMonth.endOf('month').toDate(),
      ]
    }
  }
}

/**
 * Epoch-second bounds the driver score endpoints expect for a fleet-local range. Two reversed
 * bounds are sorted, as the server does before it validates them.
 */
export const toDriverScoreRequestRange = (
  range: DateRange,
): { timestampFrom: number; timestampTo: number } => {
  const [from, to] = range[0].getTime() <= range[1].getTime() ? range : [range[1], range[0]]

  return { timestampFrom: from.getTime() / 1000, timestampTo: to.getTime() / 1000 }
}

export const applyDriverScoreDateRange = (
  queryProps: DataQueryProps,
  dateRange: DateRange | PreDefinedRange | undefined,
  defaultDateRange: PreDefinedRange | undefined,
  defaultDateRangeFn: (() => DateRange | PreDefinedRange) | undefined,
  timezone: string,
  options: DriverScoreRangeOptions = {},
) => {
  const range = getDriverScoreDateRange(
    dateRange,
    defaultDateRange,
    defaultDateRangeFn,
    resolveDriverScoreTimezone(timezone),
    options,
  )

  if (!range) {
    throw new Error('Unsupported driver score date range')
  }

  queryProps.filter = { ...(queryProps.filter ?? {}), ...toDriverScoreRequestRange(range) }
}

/**
 * Fleet-local "yesterday" as the browser wall-clock date the date picker compares against.
 * Today is never selectable because its fleet-local day has not closed yet.
 */
export const getDriverScoreMaxSelectableDate = (timezone: string, now = new Date()): Date => {
  const yesterday = moment(now).tz(resolveDriverScoreTimezone(timezone)).subtract(1, 'day')
  return new Date(yesterday.year(), yesterday.month(), yesterday.date())
}

/** Milliseconds until the next fleet-local midnight, when "yesterday" moves forward. */
export const msUntilDriverScoreDayCloses = (timezone: string, now = new Date()): number => {
  const nextMidnight = moment(now)
    .tz(resolveDriverScoreTimezone(timezone))
    .add(1, 'day')
    .startOf('day')
  return Math.max(0, nextMidnight.valueOf() - now.getTime())
}

const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD'
const UTC_DAY_MS = 24 * 60 * 60 * 1000

/**
 * Why a user-supplied range is not a Driving score period. The server answers all of them with
 * 400; the client tells them apart only to explain and to test.
 */
export type DriverScoreRangeIssue = 'incomplete' | 'too-short' | 'too-long' | 'not-closed'

export interface DriverScoreCalendarRange {
  /** Fleet-local calendar dates, `YYYY-MM-DD`. */
  startDate: string
  endDate: string
  /** Inclusive number of those dates. */
  dayCount: number
}

export type DriverScoreRangeCheck =
  | { valid: true; range: DriverScoreCalendarRange }
  | { valid: false; issue: DriverScoreRangeIssue }

const utcDay = (date: string): number => {
  const [year, month, day] = date.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

/**
 * Inclusive number of calendar dates between two `YYYY-MM-DD` dates. Dates carry no clock, so a
 * 23- or 25-hour DST day counts as one day, which elapsed milliseconds cannot promise.
 */
export const countCalendarDays = (startDate: string, endDate: string): number =>
  Math.round((utcDay(endDate) - utcDay(startDate)) / UTC_DAY_MS) + 1

/** Adds calendar days to a `YYYY-MM-DD` date. */
export const shiftCalendarDate = (date: string, days: number): string =>
  moment.utc(utcDay(date) + days * UTC_DAY_MS).format(CALENDAR_DATE_FORMAT)

export const toFleetLocalDate = (instant: Date | number, timezone: string): string =>
  moment(instant).tz(resolveDriverScoreTimezone(timezone)).format(CALENDAR_DATE_FORMAT)

/**
 * The calendar date a date picker shows for `date`: the date's own fields, which are already the
 * Fleet's wall clock whenever the picker shows a timezone override.
 */
export const toDisplayedCalendarDate = (date: Date | null | undefined): string | null =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? moment(date).format(CALENDAR_DATE_FORMAT)
    : null

/** Fleet-local yesterday: the latest date a user-selected range may end on. */
export const getDriverScoreMaxEndDate = (timezone: string, now = new Date()): string =>
  moment(now)
    .tz(resolveDriverScoreTimezone(timezone))
    .subtract(1, 'day')
    .format(CALENDAR_DATE_FORMAT)

/**
 * The range policy on Fleet-local dates: 7 to 31 inclusive dates, ending on `maxEndDate` or
 * earlier, both bounds present. Two reversed bounds are sorted. A missing bound is never
 * defaulted here; only the Previous 7 days default has no bounds, and the caller owns it.
 */
export const checkDriverScoreCalendarDates = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  maxEndDate: string,
): DriverScoreRangeCheck => {
  if (!startDate || !endDate) {
    return { valid: false, issue: 'incomplete' }
  }

  const [from, to] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate]
  const dayCount = countCalendarDays(from, to)

  if (dayCount < DriverScoreRangeLimits.minDays) {
    return { valid: false, issue: 'too-short' }
  }
  if (dayCount > DriverScoreRangeLimits.maxDays) {
    return { valid: false, issue: 'too-long' }
  }
  if (to > maxEndDate) {
    return { valid: false, issue: 'not-closed' }
  }

  return { valid: true, range: { startDate: from, endDate: to, dayCount } }
}

const isUsableInstant = (value: unknown): value is Date | number =>
  value instanceof Date
    ? !Number.isNaN(value.getTime())
    : typeof value === 'number' && Number.isFinite(value)

/** The range policy for two instants, read as Fleet-local dates. */
export const checkDriverScoreInstants = (
  from: unknown,
  to: unknown,
  timezone: string,
  now = new Date(),
): DriverScoreRangeCheck => {
  if (!isUsableInstant(from) || !isUsableInstant(to)) {
    return { valid: false, issue: 'incomplete' }
  }

  return checkDriverScoreCalendarDates(
    toFleetLocalDate(from, timezone),
    toFleetLocalDate(to, timezone),
    getDriverScoreMaxEndDate(timezone, now),
  )
}

/**
 * The range policy for a range the page holds, however it got there: picked, restored from
 * storage or navigation state. It is resolved exactly as the request resolves it. No range at all
 * is the Previous 7 days default, which is valid; a half-filled or unparsable one is not.
 */
export const checkDriverScoreDateRange = (
  dateRange: DateRange | PreDefinedRange | null | undefined,
  timezone: string,
  options: DriverScoreRangeOptions = {},
): DriverScoreRangeCheck => {
  const resolvedTimezone = resolveDriverScoreTimezone(timezone)

  if (
    Array.isArray(dateRange) &&
    !(isUsableInstant(dateRange[0]) && isUsableInstant(dateRange[1]))
  ) {
    return { valid: false, issue: 'incomplete' }
  }

  const range = getDriverScoreDateRange(
    dateRange ?? undefined,
    PreDefinedRange.Prev7Days,
    undefined,
    resolvedTimezone,
    options,
  )

  return range
    ? checkDriverScoreInstants(range[0], range[1], resolvedTimezone)
    : { valid: false, issue: 'incomplete' }
}
